import { useEffect, useState } from 'react';

import { getDetail } from './analyTransport';
import { takeMediaOf } from './takeMedia';

import type { TakeMedia } from './takeMedia';

/**
 * useTakeMedia — 서버 회차의 **다시 들을 수 있는 녹음 목록**. 없으면 빈 배열이다.
 *
 * 조회 실패·API 꺼짐·응답 모양 이상·`analyId` 없음을 전부 빈 배열로 접는 한 곳이다 —
 * 호출부(재생 카드)는 "비면 숨긴다"만 안다(ADR-0012 "미측정은 숨긴다"와 같은 방향).
 * 주소는 저장하지 않고 열 때마다 읽는다(ADR-0007: 주소 형태가 바뀔 수 있다).
 *
 * **같은 id 의 요청은 하나로 합친다** — 서버 상세는 조회에서 하위 동기화 쓰기를 한 번
 * 일으키므로(`getDetail`) StrictMode 가 effect 를 두 번 태워도 호출은 한 번이어야 한다.
 * 모양은 `analyTransport.ts`의 `creating`과 같다(맨 프로미스를 쥐고 정리는 따로 `void`).
 */
const inflight = new Map<string, Promise<TakeMedia[]>>();

const fetchTakeMedia = (analyId: string): Promise<TakeMedia[]> => {
  let promise = inflight.get(analyId);
  if (!promise) {
    promise = getDetail(analyId)
      .then((view) => (view ? takeMediaOf(view) : []))
      .catch(() => []);
    inflight.set(analyId, promise);
    void promise.finally(() => inflight.delete(analyId));
  }
  return promise;
};

export const useTakeMedia = (analyId?: string): TakeMedia[] => {
  const [media, setMedia] = useState<TakeMedia[]>([]);

  useEffect(() => {
    if (!analyId) {
      return;
    }
    let live = true;
    void fetchTakeMedia(analyId).then((list) => {
      if (live) {
        setMedia(list);
      }
    });
    return () => {
      live = false;
      // 다른 회차로 바뀌면 앞 회차의 목록이 한 렌더라도 남지 않게 비운다.
      setMedia([]);
    };
  }, [analyId]);

  return analyId ? media : [];
};
